import { createHttpHeader } from '@/utils/network/createHTTPHeaders';
import { fetchMethods } from '@/types/FetchService.types';
import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';
import { fetchQuery } from '@/utils/queryClient';
import { IAccessToken, IPasskeyParams } from '@/store/models/auth.model';
import { decryptTreezorResponse } from '@/services/encryption/decryptTreezorResponse';
import { IAuthPasskey } from '@/features/dashboard-users/store/support-user.model';

export class Auth2FaService {
  static queryKey = 'auth_2fa';

  static createPasskey = async (newPasskey: IPasskeyParams): Promise<IAuthPasskey> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [
          this.queryKey,
          { action: 'createPasskey', method: fetchMethods.POST, id: newPasskey.supportUserId, params: newPasskey },
        ],
        `${config.api.baseUrl}/core-connect/dashboard/2FA/secureKeys`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify({ ...newPasskey }),
        },
      );

      return await decryptTreezorResponse(response, { isSingle: true });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static getPasskey = async (passkeyId: string): Promise<IAuthPasskey> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { action: 'getPasskey', method: fetchMethods.GET, id: passkeyId }],
        `${config.api.baseUrl}/core-connect/dashboard/2FA/secureKeys/${passkeyId}`,
        {
          headers: createHttpHeader(),
        },
      );

      return await decryptTreezorResponse(response, { isSingle: true });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static deletePasskey = async (passkeyId: string): Promise<IAuthPasskey> => {
    try {
      const config = getStaticDashboardConfig();

      const response = await fetchQuery(
        [this.queryKey, { action: 'deletePasskey', method: fetchMethods.DELETE, id: passkeyId }],
        `${config.api.baseUrl}/core-connect/dashboard/2FA/secureKeys/${passkeyId}`,
        {
          headers: createHttpHeader(),
        },
      );

      return await decryptTreezorResponse(response, { isSingle: true });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static authenticateWithPasskey = async (refreshToken: string, credentials: string): Promise<IAccessToken> => {
    try {
      const config = getStaticDashboardConfig();
      const body = JSON.stringify({
        client_id: config.api.oauth.clientId,
        refresh_token: refreshToken,
        grant_type: 'refresh_token',
        credentials,
      });

      const response = await fetchQuery(
        [this.queryKey, { action: 'authenticate2Fa', method: fetchMethods.POST }],
        `${config.api.oauth.path}`,
        {
          headers: createHttpHeader(),
          body,
        },
      );

      return await decryptTreezorResponse(response, { isSingle: true });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static generateChallenge = async (): Promise<{ challenge: string }> => {
    try {
      const config = getStaticDashboardConfig();
      const response = await fetchQuery(
        [this.queryKey, { action: 'generate-challenge', method: fetchMethods.POST }],
        `${config.api.baseUrl}/core-connect/dashboard/2FA/options`,
        {
          headers: createHttpHeader(),
        },
      );

      return await decryptTreezorResponse(response, { isSingle: true });
    } catch (err) {
      return Promise.reject(err);
    }
  };
}
export default Auth2FaService;
